/*
 * contact-form-enrich.js — Enriched contact-form interceptor (Chunk D of
 * PLAN_CONTACT_FORM_ENRICHMENT).
 *
 * Behavior:
 *   - Intercepts the submit BUTTON click in capture phase, not the form
 *     submit event. Shopify auto-attaches invisible hCaptcha to contact
 *     forms when bot scoring is high; that handler calls form.submit()
 *     directly, which bypasses the form 'submit' event entirely. Capture
 *     phase + button-click + setting type="button" defangs that path.
 *   - Feature-flagged via data-enrichment-enabled="true" on the form
 *     element (set inline by Chunk E). When the flag is anything else,
 *     the click is allowed to proceed normally.
 *   - Happy path: POST {name,email,phone,country,comment,ts,token} as
 *     JSON to the Cloudflare Worker /contact-enrich endpoint with an
 *     Idempotency-Key header. On 2xx, render the same success markup
 *     the Liquid template emits when form.posted_successfully? is true.
 *     NOTE on 2xx semantics: the Worker now validates synchronously and
 *     returns a fast 202 ("accepted") — the enriched CS email is built
 *     and sent ASYNCHRONOUSLY by the Worker after the response (ctx.
 *     waitUntil). So a 2xx means "accepted, the Worker will send the
 *     email" — NOT "the email is already sent". The Worker guarantees a
 *     base enriched email is always sent even if the AI step fails. This
 *     client behavior is UNCHANGED: any 2xx still suppresses the native
 *     fallback (the submission was accepted).
 *   - Native fallback path: on 5xx, network error, timeout, or 404
 *     (route missing), restore the button type, set a sentinel flag,
 *     and call form.submit() to fire the original Shopify POST.
 *     UNCHANGED by the async conversion — a 5xx/timeout/404 still means
 *     the Worker did NOT accept the submission, so the native form is
 *     the only delivery path.
 *   - 4xx (other than 404) means the Worker rejected the input
 *     (validation/token/rate-limit). Show a generic error banner; do
 *     NOT fall back (Shopify would just accept the same bad input AND
 *     would not get the enriched payload anyway). UNCHANGED — validation
 *     failures are still returned synchronously before the 202.
 *
 * Design constraints (from plan MUST NOT):
 *   - No jQuery / no external dependencies (vanilla JS, IIFE).
 *   - Read fields by id only — name= attributes are translated per locale.
 *   - Reuse the same Idempotency-Key for repeat fetches of the same
 *     submission (defeats KV dedupe otherwise). A fresh page load gets a
 *     fresh key.
 *   - No retry against the Worker — straight to native fallback on failure.
 *   - No reading of cookies / localStorage / session data — payload is
 *     form fields only.
 *   - No logging of raw form values (PII).
 */
(function () {
  'use strict';

  var ENRICH_URL = 'https://downloads-api.nightingale.com/contact-enrich';
  var FETCH_TIMEOUT_MS = 8000;

  function init() {
    var form = document.querySelector('#ContactForm');
    if (!form) return;
    if (form.dataset.enrichmentEnabled !== 'true') return;

    form.addEventListener('submit', handleSubmit, true);

    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) {
      btn.addEventListener('click', handleButtonClick, true);
      btn.setAttribute('data-original-type', btn.getAttribute('type') || 'submit');
      btn.setAttribute('type', 'button');
    }
  }

  function handleButtonClick(event) {
    if (event.defaultPrevented) return;
    var form = event.currentTarget.form || document.querySelector('#ContactForm');
    if (!form) return;
    if (form.dataset.fallbackTriggered === 'true') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    runEnrichment(form);
  }

  function handleSubmit(event) {
    var form = event.currentTarget;
    if (form.dataset.fallbackTriggered === 'true') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    runEnrichment(form);
  }

  function runEnrichment(form) {
    var payload = buildPayload(form);

    var idempotencyKey =
      form.dataset.idempotencyKey ||
      (form.dataset.idempotencyKey = crypto.randomUUID());

    fetch(ENRICH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(payload),
      credentials: 'omit',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })
      .then(function (response) {
        if (response.ok) {
          showSuccess(form);
          return;
        }
        if (response.status === 404 || response.status >= 500) {
          submitNativeFallback(form);
          return;
        }
        if (response.status === 403) {
          // A stale page or a key rotation (invalid_token / token_expired): the
          // native Shopify form still delivers the message, so nothing is lost.
          return response.json().catch(function () { return {}; }).then(function (body) {
            if (body && (body.error === 'invalid_token' || body.error === 'token_expired')) {
              submitNativeFallback(form);
              return;
            }
            console.warn('[contact-enrich] worker rejected submission', response.status);
            showError(form);
          });
        }
        console.warn('[contact-enrich] worker rejected submission', response.status);
        showError(form);
      })
      .catch(function (err) {
        console.warn('[contact-enrich] worker error', err && err.name);
        submitNativeFallback(form);
      });
  }

  function buildPayload(form) {
    var country = '';
    var countryEl = form.querySelector('#ContactForm-country');
    if (countryEl && typeof countryEl.value === 'string') {
      country = countryEl.value;
    }

    return {
      name: readValue(form, '#ContactForm-name'),
      email: readValue(form, '#ContactForm-email'),
      phone: readValue(form, '#ContactForm-phone'),
      country: country,
      comment: readValue(form, '#ContactForm-body'),
      ts: form.dataset.enrichmentTs || '',
      token: form.dataset.enrichmentToken || '',
    };
  }

  function readValue(form, selector) {
    var el = form.querySelector(selector);
    if (!el || typeof el.value !== 'string') return '';
    return el.value;
  }

  function showSuccess(form) {
    var existing = form.querySelector('.contact-success-banner');
    if (existing) return;

    var heading = document.createElement('h2');
    heading.className = 'contact-success-banner form-status form-status-list form__message';
    heading.setAttribute('tabindex', '-1');
    heading.setAttribute('autofocus', '');
    heading.style.cssText = 'background:#2e7d32;color:#fff;padding:18px 24px;margin:0 0 24px;border-radius:4px;font-size:1.1rem;font-weight:600;text-align:center;';
    heading.textContent = 'Thanks for contacting us. We’ll get back to you as soon as possible.';

    form.insertBefore(heading, form.firstChild);

    var fields = ['#ContactForm-name', '#ContactForm-email', '#ContactForm-phone', '#ContactForm-body'];
    for (var i = 0; i < fields.length; i++) {
      var el = form.querySelector(fields[i]);
      if (el) el.value = '';
    }

    try { heading.focus(); } catch (_e) { /* focus may fail in some browsers */ }
  }

  function showError(form) {
    var existing = form.querySelector('.form__message[data-enrich-error]');
    if (existing) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'form__message';
    wrapper.setAttribute('data-enrich-error', 'true');

    var heading = document.createElement('h2');
    heading.className = 'form-status caption-large text-body';
    heading.setAttribute('role', 'alert');
    heading.setAttribute('tabindex', '-1');
    heading.textContent = 'There was a problem submitting your message. Please try again.';
    wrapper.appendChild(heading);

    form.insertBefore(wrapper, form.firstChild);
    try { heading.focus(); } catch (_e) { /* focus may fail in some browsers */ }
  }

  function submitNativeFallback(form) {
    form.dataset.fallbackTriggered = 'true';
    var btn = form.querySelector('button[type="button"][data-original-type], input[type="button"][data-original-type]');
    if (btn) {
      btn.setAttribute('type', btn.getAttribute('data-original-type') || 'submit');
    }
    form.submit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
